import { useEffect, useState } from 'react';

/**
 * Generic hook for managing state in localStorage
 * @template T - Type of the value stored in localStorage
 * @param key - LocalStorage key. When undefined, behaves as plain useState (no persistence)
 * @param initialValue - Initial value if nothing is stored
 * @returns Tuple with [value, setValue] similar to useState
 */
export function useLocalStorage<T>(
  key: string | undefined,
  initialValue: T,
): [T, (value: T | ((prevValue: T) => T)) => void] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (!key) {
      return initialValue;
    }
    try {
      const item = window.localStorage.getItem(key);
      if (item) {
        return JSON.parse(item) as T;
      }
      return initialValue;
    } catch (error) {
      console.error(
        `Failed to load value from localStorage for key "${key}":`,
        error,
      );
      return initialValue;
    }
  });

  const setValue = (value: T | ((prevValue: T) => T)) => {
    try {
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);

      if (!key) {
        return;
      }

      if (valueToStore === null || valueToStore === undefined) {
        window.localStorage.removeItem(key);
      } else {
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
      }
    } catch (error) {
      console.error(
        `Failed to save value to localStorage for key "${key}":`,
        error,
      );
    }
  };

  useEffect(() => {
    if (!key) {
      return;
    }
    try {
      if (storedValue === null || storedValue === undefined) {
        window.localStorage.removeItem(key);
      } else {
        window.localStorage.setItem(key, JSON.stringify(storedValue));
      }
    } catch (error) {
      console.error(
        `Failed to sync value to localStorage for key "${key}":`,
        error,
      );
    }
  }, [key, storedValue]);

  return [storedValue, setValue];
}
